// Customer Account Page - Profile, Orders, Settings
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export default function CustomerAccount() {
  const { user, userProfile, signOut, updateUserProfile, resendVerificationEmail, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Form states
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    phone: '',
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA'
    }
  });

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/customer-login');
      return;
    }

    // Load profile data
    if (userProfile) {
      setProfileForm({
        displayName: userProfile.displayName || user.displayName || '',
        phone: userProfile.phone || '',
        shippingAddress: userProfile.shippingAddress || {
          street: '',
          city: '',
          state: '',
          zip: '',
          country: 'USA'
        }
      });
    }

    // Load orders
    loadOrders();
  }, [user, userProfile, navigate]);

  const loadOrders = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/customers/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    try {
      setLoading(true);
      await updateUserProfile(profileForm);
      setMessage('Profile updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return setError('Passwords do not match');
    }

    if (passwordForm.newPassword.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    try {
      setLoading(true);
      await changePassword(passwordForm.newPassword);
      setMessage('Password updated successfully!');
      setPasswordForm({ newPassword: '', confirmPassword: '' });
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update password. You may need to sign in again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail();
      setMessage('Verification email sent! Please check your inbox.');
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      setError('Failed to send verification email');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-cream-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold text-ink">My Account</h1>
          <p className="text-ink-soft mt-1">Welcome back, {user.displayName || user.email}!</p>
        </div>

        {/* Email Verification Banner */}
        {!user.emailVerified && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-yellow-800 font-medium">Please verify your email address</p>
                <p className="text-yellow-700 text-sm mt-1">
                  Check your inbox for a verification link. Didn't receive it?
                </p>
              </div>
              <button
                onClick={handleResendVerification}
                className="ml-4 px-4 py-2 bg-yellow-600 text-white rounded-lg text-sm font-semibold hover:bg-yellow-700"
              >
                Resend Email
              </button>
            </div>
          </div>
        )}

        {/* Messages */}
        {message && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800">{message}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-cream-300">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'profile'
                    ? 'border-sage-700 text-sage-700'
                    : 'border-transparent text-ink-muted hover:text-ink-soft hover:border-cream-300'
                }`}
              >
                👤 Profile
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'orders'
                    ? 'border-sage-700 text-sage-700'
                    : 'border-transparent text-ink-muted hover:text-ink-soft hover:border-cream-300'
                }`}
              >
                📦 Orders ({orders.length})
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'security'
                    ? 'border-sage-700 text-sage-700'
                    : 'border-transparent text-ink-muted hover:text-ink-soft hover:border-cream-300'
                }`}
              >
                🔒 Security
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileUpdate} className="space-y-6 max-w-2xl">
                <div>
                  <h3 className="text-lg font-semibold text-ink mb-4">Personal Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.displayName}
                        onChange={(e) => setProfileForm({ ...profileForm, displayName: e.target.value })}
                        className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={user.email}
                        disabled
                        className="w-full px-4 py-2 border border-cream-300 rounded-lg bg-cream-100 text-ink-muted"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-ink mb-4">Shipping Address</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={profileForm.shippingAddress.street}
                        onChange={(e) => setProfileForm({
                          ...profileForm,
                          shippingAddress: { ...profileForm.shippingAddress, street: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500"
                        placeholder="123 Main St"
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-ink-soft mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          value={profileForm.shippingAddress.city}
                          onChange={(e) => setProfileForm({
                            ...profileForm,
                            shippingAddress: { ...profileForm.shippingAddress, city: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-ink-soft mb-2">
                          State
                        </label>
                        <input
                          type="text"
                          value={profileForm.shippingAddress.state}
                          onChange={(e) => setProfileForm({
                            ...profileForm,
                            shippingAddress: { ...profileForm.shippingAddress, state: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500"
                          placeholder="GA"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-ink-soft mb-2">
                          ZIP Code
                        </label>
                        <input
                          type="text"
                          value={profileForm.shippingAddress.zip}
                          onChange={(e) => setProfileForm({
                            ...profileForm,
                            shippingAddress: { ...profileForm.shippingAddress, zip: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-sage-700 text-white rounded-lg font-semibold hover:bg-sage-800 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-ink">Order History</h3>
                  <Link to="/shop" className="text-sage-700 hover:text-sage-800 font-medium">
                    Continue Shopping →
                  </Link>
                </div>

                {orders.length === 0 ? (
                  <div className="text-center py-12 bg-cream-100 rounded-lg">
                    <p className="text-ink-soft mb-4">You haven't placed any orders yet</p>
                    <Link
                      to="/shop"
                      className="inline-block px-6 py-3 bg-sage-700 text-white rounded-lg font-semibold hover:bg-sage-800"
                    >
                      Start Shopping
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="border border-cream-300 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-ink">Order #{order.id}</h4>
                            <p className="text-sm text-ink-soft mt-1">
                              Placed on {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            order.status === 'completed' ? 'bg-green-100 text-green-800' :
                            order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'shipped' ? 'bg-sage-100 text-sage-900' :
                            'bg-cream-200 text-ink'
                          }`}>
                            {order.status}
                          </span>
                        </div>

                        <div className="space-y-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="text-ink-soft">{item.productName} × {item.quantity}</span>
                              <span className="font-medium">${item.price.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>

                        <div className="border-t border-cream-300 mt-4 pt-4 flex items-center justify-between">
                          <span className="font-semibold text-ink">Total</span>
                          <span className="text-lg font-bold text-sage-700">${order.total.toFixed(2)}</span>
                        </div>

                        {order.trackingNumber && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Tracking:</strong> {order.trackingNumber}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6 max-w-2xl">
                <div>
                  <h3 className="text-lg font-semibold text-ink mb-4">Change Password</h3>
                  
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500"
                        placeholder="••••••••"
                      />
                      <p className="mt-1 text-xs text-ink-muted">Must be at least 6 characters</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500"
                        placeholder="••••••••"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-3 bg-sage-700 text-white rounded-lg font-semibold hover:bg-sage-800 disabled:opacity-50"
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>
                </div>

                <div className="border-t border-cream-300 pt-6">
                  <button
                    onClick={handleSignOut}
                    className="px-6 py-3 bg-cream-300 text-ink-soft rounded-lg font-semibold hover:bg-gray-300"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
